import { useEffect, useRef } from "react";

interface NodeSource {
  label?: string;
  radius: number;
  speed: number;
  phase: number;
  tiltX: number;
  tiltY: number;
  tiltZ: number;
  size: number;
  particles: { progress: number; speed: number }[];
}

export default function CombavaOrbitalCore() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Mouse interaction for subtle 3D parallax
  const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = 0;
    let height = 0;
    let dpr = 1;

    // Node definitions: Data sources orbiting at varying distances & 3D inclinations (slowed down for graceful motion)
    const nodes: NodeSource[] = [
      { label: "", radius: 145, speed: 0.0018, phase: 0.2, tiltX: 0.45, tiltY: 0.3, tiltZ: 0.2, size: 5, particles: [{ progress: 0.1, speed: 0.0025 }, { progress: 0.6, speed: 0.0025 }] },
      { label: "", radius: 105, speed: -0.0022, phase: 1.4, tiltX: -0.5, tiltY: 0.6, tiltZ: -0.3, size: 5, particles: [{ progress: 0.3, speed: 0.003 }, { progress: 0.8, speed: 0.003 }] },
      { label: "", radius: 130, speed: 0.0016, phase: 2.8, tiltX: 0.7, tiltY: -0.4, tiltZ: 0.5, size: 4.5, particles: [{ progress: 0.2, speed: 0.0028 }, { progress: 0.7, speed: 0.0028 }] },
      { label: "", radius: 160, speed: -0.0014, phase: 3.6, tiltX: -0.35, tiltY: -0.7, tiltZ: 0.4, size: 5, particles: [{ progress: 0.4, speed: 0.0022 }, { progress: 0.9, speed: 0.0022 }] },
      { label: "", radius: 95, speed: 0.0025, phase: 4.5, tiltX: 0.2, tiltY: 0.8, tiltZ: -0.4, size: 4.5, particles: [{ progress: 0.15, speed: 0.0035 }] },
      { label: "", radius: 165, speed: 0.0012, phase: 5.3, tiltX: -0.65, tiltY: 0.2, tiltZ: 0.6, size: 4.5, particles: [{ progress: 0.5, speed: 0.0022 }] },
      { label: "", radius: 115, speed: -0.0020, phase: 0.9, tiltX: 0.6, tiltY: 0.5, tiltZ: -0.5, size: 4, particles: [{ progress: 0.35, speed: 0.003 }] },
      { label: "", radius: 140, speed: 0.0017, phase: 2.1, tiltX: -0.25, tiltY: -0.5, tiltZ: -0.2, size: 4.5, particles: [{ progress: 0.25, speed: 0.0026 }] },
      { label: "", radius: 150, speed: -0.0015, phase: 3.9, tiltX: 0.55, tiltY: -0.6, tiltZ: 0.1, size: 5, particles: [{ progress: 0.45, speed: 0.0025 }] },
      { label: "", radius: 110, speed: 0.0023, phase: 4.9, tiltX: -0.4, tiltY: 0.4, tiltZ: 0.7, size: 4, particles: [{ progress: 0.05, speed: 0.0032 }] },
    ];

    const resize = () => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener("resize", resize);

    let time = 0;
    let corePulse = 0;

    // 3D rotation helper
    const rotate3D = (
      x: number,
      y: number,
      z: number,
      rotX: number,
      rotY: number,
      rotZ: number
    ) => {
      // Rotate around X
      let y1 = y * Math.cos(rotX) - z * Math.sin(rotX);
      let z1 = y * Math.sin(rotX) + z * Math.cos(rotX);

      // Rotate around Y
      let x2 = x * Math.cos(rotY) + z1 * Math.sin(rotY);
      let z2 = -x * Math.sin(rotY) + z1 * Math.cos(rotY);

      // Rotate around Z
      let x3 = x2 * Math.cos(rotZ) - y1 * Math.sin(rotZ);
      let y3 = x2 * Math.sin(rotZ) + y1 * Math.cos(rotZ);

      return { x: x3, y: y3, z: z2 };
    };

    const render = () => {
      time += 1;

      // Smooth mouse easing for parallax tilt
      mouseRef.current.x += (mouseRef.current.targetX - mouseRef.current.x) * 0.05;
      mouseRef.current.y += (mouseRef.current.targetY - mouseRef.current.y) * 0.05;

      ctx.clearRect(0, 0, width, height);

      const centerX = width / 2;
      const centerY = height / 2;
      const focalLength = 360;

      // Global slow scene rotation plus mouse parallax (gentle and majestic)
      const globalRotY = time * 0.0006 + mouseRef.current.x * 0.25;
      const globalRotX = 0.22 + mouseRef.current.y * 0.25;
      const globalRotZ = time * 0.0002;

      // ─── 1. Calculate 3D Projected Positions for All Nodes ───
      interface ProjectedNode {
        node: NodeSource;
        px: number;
        py: number;
        pz: number;
        projX: number;
        projY: number;
        scale: number;
        alpha: number;
      }

      const projectedNodes: ProjectedNode[] = nodes.map((node) => {
        // Position on its local tilted orbit
        const currentAngle = node.phase + time * node.speed;
        const localX = Math.cos(currentAngle) * node.radius;
        const localY = Math.sin(currentAngle) * node.radius;
        const localZ = 0;

        // Apply node's unique orbital plane tilt
        const tilted = rotate3D(localX, localY, localZ, node.tiltX, node.tiltY, node.tiltZ);

        // Apply global scene rotation & mouse parallax
        const world = rotate3D(tilted.x, tilted.y, tilted.z, globalRotX, globalRotY, globalRotZ);

        // Perspective projection
        const scale = focalLength / (focalLength + world.z);
        const projX = centerX + world.x * scale;
        const projY = centerY + world.y * scale;

        // Depth cue: alpha between 0.35 (back) and 1.0 (front)
        const depthNorm = (world.z + 160) / 320;
        const alpha = Math.max(0.3, Math.min(1.0, 1.0 - depthNorm * 0.6));

        return {
          node,
          px: world.x,
          py: world.y,
          pz: world.z,
          projX,
          projY,
          scale,
          alpha,
        };
      });

      // Sort back-to-front for proper depth occlusion
      projectedNodes.sort((a, b) => b.pz - a.pz);

      // ─── 2. Draw Citrus Segment Geometric Webbing (Background Faint Petals) ───
      const carpels = 10;
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(time * 0.0003);
      for (let i = 0; i < carpels; i++) {
        const angle = (i * 2 * Math.PI) / carpels;
        const nextAngle = ((i + 1) * 2 * Math.PI) / carpels;
        const innerR = 24;
        const outerR = 68;

        ctx.beginPath();
        ctx.moveTo(Math.cos(angle) * innerR, Math.sin(angle) * innerR);
        ctx.quadraticCurveTo(
          Math.cos((angle + nextAngle) / 2) * outerR * 1.1,
          Math.sin((angle + nextAngle) / 2) * outerR * 1.1,
          Math.cos(nextAngle) * innerR,
          Math.sin(nextAngle) * innerR
        );
        ctx.strokeStyle = "rgba(21, 222, 155, 0.08)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.restore();

      // ─── 3. Draw Connecting Rays & Inward Streaming Particles ───
      projectedNodes.forEach((pn) => {
        const { projX, projY, scale, alpha, node, px, py, pz } = pn;

        // Ray Connecting Node to Core
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(projX, projY);

        // Ray Gradient (White/Green near core, dim near node)
        const rayGrad = ctx.createLinearGradient(centerX, centerY, projX, projY);
        rayGrad.addColorStop(0, `rgba(255, 255, 255, ${0.4 * alpha})`);
        rayGrad.addColorStop(0.3, `rgba(21, 222, 155, ${0.45 * alpha})`);
        rayGrad.addColorStop(1, `rgba(255, 255, 255, ${0.15 * alpha})`);

        ctx.strokeStyle = rayGrad;
        ctx.lineWidth = Math.max(0.75, 1.2 * scale);
        ctx.stroke();

        // Data Movement: Inward Streaming Packets (traveling from node 1.0 -> core 0.0)
        node.particles.forEach((p) => {
          p.progress += p.speed;
          if (p.progress >= 1.0) {
            p.progress = 0.0;
            // Impulse on core arrival
            corePulse = Math.min(corePulse + 0.15, 1.0);
          }

          // Inward position: 0 is at node, 1 is at core
          const tPos = 1.0 - p.progress; // 1 = at node, 0 = at core
          const partX3D = px * tPos;
          const partY3D = py * tPos;
          const partZ3D = pz * tPos;

          const pScale = focalLength / (focalLength + partZ3D);
          const partScreenX = centerX + partX3D * pScale;
          const partScreenY = centerY + partY3D * pScale;

          // Packet Glow
          const partSize = Math.max(1.8, 3.2 * pScale);
          ctx.beginPath();
          ctx.arc(partScreenX, partScreenY, partSize * 1.6, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(21, 222, 155, ${0.45 * alpha})`;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(partScreenX, partScreenY, partSize, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${0.95 * alpha})`;
          ctx.fill();
        });
      });

      // ─── 4. Draw Central Citrus Core (Luminous Hub) ───
      corePulse *= 0.94; // Exponential decay
      const baseCoreRadius = 22;
      const pulseExtra = corePulse * 6 + Math.sin(time * 0.05) * 1.5;
      const coreR = baseCoreRadius + pulseExtra;

      // Outer Corona Glow
      const corona = ctx.createRadialGradient(
        centerX,
        centerY,
        coreR * 0.3,
        centerX,
        centerY,
        coreR * 3.5
      );
      corona.addColorStop(0, "rgba(21, 222, 155, 0.45)");
      corona.addColorStop(0.4, "rgba(21, 222, 155, 0.18)");
      corona.addColorStop(1, "rgba(21, 222, 155, 0)");

      ctx.beginPath();
      ctx.arc(centerX, centerY, coreR * 3.5, 0, Math.PI * 2);
      ctx.fillStyle = corona;
      ctx.fill();

      // Middle Citrus Pith Ring
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreR * 1.4, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      ctx.lineWidth = 1.2;
      ctx.stroke();

      // Secondary Dashed Orbit Ring around Core
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(-time * 0.004);
      ctx.beginPath();
      ctx.arc(0, 0, coreR * 1.8, 0, Math.PI * 2);
      ctx.setLineDash([4, 6]);
      ctx.strokeStyle = "rgba(21, 222, 155, 0.3)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();

      // Inner Solid Core
      const innerGrad = ctx.createRadialGradient(
        centerX - coreR * 0.25,
        centerY - coreR * 0.25,
        coreR * 0.1,
        centerX,
        centerY,
        coreR
      );
      innerGrad.addColorStop(0, "#FFFFFF");
      innerGrad.addColorStop(0.65, "#E2E8F0");
      innerGrad.addColorStop(1, "#15DE9B");

      ctx.beginPath();
      ctx.arc(centerX, centerY, coreR, 0, Math.PI * 2);
      ctx.fillStyle = innerGrad;
      ctx.shadowColor = "#15DE9B";
      ctx.shadowBlur = 16 + corePulse * 12;
      ctx.fill();
      ctx.shadowBlur = 0; // Reset

      // ─── 5. Draw Orbiting Nodes & Labels ───
      projectedNodes.forEach((pn) => {
        const { projX, projY, scale, alpha, node } = pn;
        const nodeSize = node.size * scale;

        // Outer Glow Ring
        ctx.beginPath();
        ctx.arc(projX, projY, nodeSize * 2.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(21, 222, 155, ${0.25 * alpha})`;
        ctx.fill();

        // Node Border Ring
        ctx.beginPath();
        ctx.arc(projX, projY, nodeSize * 1.3, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.7 * alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Solid Node Core
        ctx.beginPath();
        ctx.arc(projX, projY, nodeSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(21, 222, 155, ${alpha})`;
        ctx.fill();

        // Node Bright Center
        ctx.beginPath();
        ctx.arc(projX, projY, nodeSize * 0.45, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseRef.current.targetX = x;
    mouseRef.current.targetY = y;
  };

  const handleMouseLeave = () => {
    mouseRef.current.targetX = 0;
    mouseRef.current.targetY = 0;
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative w-[320px] h-[320px] sm:w-[380px] sm:h-[380px] lg:w-[440px] lg:h-[440px] xl:w-[480px] xl:h-[480px] select-none flex items-center justify-center cursor-default"
      title="Combava Core"
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full block"
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}
